import {
	Button,
	type ColumnDef,
	cn,
	DataTable,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	useUrlState,
} from "@monbits/ui";
import {
	AlertTriangle,
	Check,
	CheckCircle,
	CircleDashed,
	Clock,
	Copy,
	ExternalLink,
	Radio,
	Wallet,
	XCircle,
} from "lucide-react";
import * as React from "react";
import { Link, Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import type {
	BlockedWallet,
	BlockedWalletAsset,
	FundReturn,
} from "src/providers/fund-return-provider";
import {
	useBlockedWallets,
	useBroadcastFundReturn,
	useFundReturns,
} from "src/queries/fund-return-queries";

type StatusFilterValue = FundReturn["status"] | "all";

const STATUS_OPTIONS: Array<{ value: StatusFilterValue; label: string }> = [
	{ value: "all", label: "All Statuses" },
	{ value: "pending", label: "Pending" },
	{ value: "inProgress", label: "In Progress" },
	{ value: "completed", label: "Completed" },
	{ value: "failed", label: "Failed" },
];

const statusConfig: Record<
	FundReturn["status"],
	{
		label: string;
		type: "default" | "info" | "warning" | "success" | "error";
		icon: typeof CheckCircle;
	}
> = {
	pending: { label: "Pending", type: "warning", icon: Clock },
	inProgress: { label: "In Progress", type: "info", icon: CircleDashed },
	completed: { label: "Completed", type: "success", icon: CheckCircle },
	failed: { label: "Failed", type: "error", icon: XCircle },
};

type WalletAssetRow = {
	wallet: BlockedWallet;
	asset: BlockedWalletAsset;
	rowKey: string;
};

export const FundReturnsList: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const [filters, setFilters] = useUrlState({
		tab: { type: "string", default: "wallets" },
		status: { type: "string", default: "" },
		page: { type: "number", default: 1 },
		limit: { type: "number", default: 20 },
	});

	const activeTab = (filters.tab === "returns" ? "returns" : "wallets") as
		| "wallets"
		| "returns";

	const statusFilter = (filters.status || "all") as StatusFilterValue;

	const walletsQuery = useBlockedWallets();

	const offset = (filters.page - 1) * filters.limit;

	const returnsQuery = useFundReturns({
		status: statusFilter === "all" ? undefined : statusFilter,
		limit: filters.limit,
		offset,
	});

	const [showBroadcastDialog, setShowBroadcastDialog] = React.useState(false);
	const [selectedReturn, setSelectedReturn] = React.useState<FundReturn | null>(
		null,
	);

	const [totpCode, setTotpCode] = React.useState("");

	const [copiedAddress, setCopiedAddress] = React.useState<string | null>(null);

	const broadcastMutation = useBroadcastFundReturn();

	const blockedWallets = walletsQuery.data ?? [];
	const fundReturns = returnsQuery.data?.fundReturns ?? [];
	const totalReturns = returnsQuery.data?.total ?? 0;

	const isLoadingWallets = walletsQuery.isLoading;
	const isLoadingReturns = returnsQuery.isLoading;

	const walletAssetRows = React.useMemo(() => {
		const rows: WalletAssetRow[] = [];
		for (const wallet of blockedWallets) {
			for (const asset of wallet.assets) {
				rows.push({
					wallet,
					asset,
					rowKey: `${wallet.id}-${asset.symbol}`,
				});
			}
		}
		return rows;
	}, [blockedWallets]);

	const basePath = "/fund-returns";

	const buildQueryString = React.useCallback(
		(nextTab: "wallets" | "returns") => {
			const params = new URLSearchParams(searchParams);
			params.set("tab", nextTab);
			return params.toString();
		},
		[searchParams],
	);

	const buildTabLink = React.useCallback(
		(nextTab: "wallets" | "returns") => {
			const queryString = buildQueryString(nextTab);
			return queryString ? `${basePath}?${queryString}` : basePath;
		},
		[buildQueryString],
	);

	const handleWalletRowClick = React.useCallback(
		(row: WalletAssetRow) => {
			const queryString = buildQueryString("wallets");
			const assetSlug = encodeURIComponent(row.asset.symbol);
			const path = `/fund-returns/wallets/${row.wallet.id}/${assetSlug}`;
			navigate(queryString ? `${path}?${queryString}` : path);
		},
		[buildQueryString, navigate],
	);

	const handleOpenBroadcastDialog = React.useCallback(
		(fundReturn: FundReturn) => {
			setSelectedReturn(fundReturn);
			setTotpCode("");
			setShowBroadcastDialog(true);
		},
		[],
	);

	const handleCloseBroadcastDialog = React.useCallback(() => {
		setShowBroadcastDialog(false);
		setSelectedReturn(null);
		setTotpCode("");
	}, []);

	const handleBroadcast = React.useCallback(async () => {
		if (!selectedReturn) return;

		if (totpCode.trim().length !== 6) {
			toast.error("Invalid 2FA code");
			return;
		}

		try {
			await broadcastMutation.mutateAsync({
				id: selectedReturn.id,
				data: { totpCode: totpCode.trim() },
			});
			toast.success("Transaction broadcasted successfully");
			handleCloseBroadcastDialog();
		} catch (error: unknown) {
			console.error("Failed to broadcast fund return:", error);
			const errorMessage =
				(error as { response?: { data?: { message?: string } } })?.response
					?.data?.message || "Failed to broadcast";
			toast.error(errorMessage);
		}
	}, [selectedReturn, totpCode, broadcastMutation, handleCloseBroadcastDialog]);

	const handleCopyAddress = React.useCallback(
		(address: string, e: React.MouseEvent) => {
			e.stopPropagation();
			navigator.clipboard.writeText(address);
			setCopiedAddress(address);
			toast.success("Address copied");
			setTimeout(() => setCopiedAddress(null), 2000);
		},
		[],
	);

	const handleReturnRowClick = React.useCallback(
		(fundReturn: FundReturn) => {
			const queryString = buildQueryString("returns");
			const path = `/fund-returns/${fundReturn.id}`;
			navigate(queryString ? `${path}?${queryString}` : path);
		},
		[buildQueryString, navigate],
	);

	const walletColumns: ColumnDef<WalletAssetRow>[] = React.useMemo(
		() => [
			{
				id: "network",
				header: "Network",
				cell: (row) => (
					<div className="flex items-center gap-2">
						<CryptoIcon ticker={row.wallet.blockchain} className="h-5 w-5" />
						<span className="text-sm">{row.wallet.blockchain}</span>
					</div>
				),
			},
			{
				id: "address",
				header: "Address",
				cell: (row) => (
					<div className="flex items-center gap-1">
						<span
							className="font-mono text-xs truncate max-w-[120px]"
							title={row.wallet.address}
						>
							{row.wallet.address.slice(0, 6)}...
							{row.wallet.address.slice(-4)}
						</span>
						<Button
							variant="ghost"
							size="icon"
							className="h-5 w-5 flex-shrink-0"
							onClick={(e) => handleCopyAddress(row.wallet.address, e)}
						>
							{copiedAddress === row.wallet.address ? (
								<Check className="h-3 w-3 text-green-600" />
							) : (
								<Copy className="h-3 w-3" />
							)}
						</Button>
					</div>
				),
			},
			{
				id: "asset",
				header: "Asset",
				cell: (row) => {
					const assetIconTicker =
						row.asset.symbol === "Unknown Token"
							? row.wallet.blockchain
							: row.asset.symbol;
					return (
						<div className="flex items-center gap-2">
							<CryptoIcon ticker={assetIconTicker} className="h-4 w-4" />
							<span className="text-sm font-medium">{row.asset.symbol}</span>
						</div>
					);
				},
			},
			{
				id: "balance",
				header: "Balance",
				cell: (row) => (
					<span className="font-mono text-sm">{row.asset.balance}</span>
				),
			},
			{
				id: "reason",
				header: "Block Reason",
				cell: (row) => (
					<div className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
						<AlertTriangle className="h-3 w-3 flex-shrink-0" />
						<span className="text-xs truncate max-w-[250px]">
							{row.wallet.blockReason}
						</span>
					</div>
				),
			},
			{
				id: "blocked",
				header: "Blocked",
				cell: (row) => <TimeLabel time={row.wallet.blockedAt} />,
			},
		],
		[copiedAddress, handleCopyAddress],
	);

	const returnColumns: ColumnDef<FundReturn>[] = React.useMemo(
		() => [
			{
				id: "id",
				header: "ID",
				cell: (fr) => <span className="font-medium">#{fr.id}</span>,
			},
			{
				id: "network",
				header: "Network",
				cell: (fr) => (
					<div className="flex items-center gap-2">
						<CryptoIcon ticker={fr.blockchain} className="h-5 w-5" />
						<span>{fr.blockchain}</span>
					</div>
				),
			},
			{
				id: "status",
				header: "Status",
				cell: (fr) => {
					const config = statusConfig[fr.status] ?? {
						label: fr.status,
						type: "default" as const,
						icon: Clock,
					};
					return (
						<StatusBadge type={config.type} icon={config.icon}>
							{config.label}
						</StatusBadge>
					);
				},
			},
			{
				id: "amount",
				header: "Amount",
				cell: (fr) => (
					<div className="flex items-center gap-2">
						<CryptoIcon ticker={fr.currency} className="h-4 w-4" />
						<span className="font-mono">{fr.amount}</span>
					</div>
				),
			},
			{
				id: "from",
				header: "From",
				cell: (fr) => (
					<span className="font-mono text-sm">
						{fr.walletAddress.slice(0, 8)}...{fr.walletAddress.slice(-6)}
					</span>
				),
			},
			{
				id: "to",
				header: "To",
				cell: (fr) => (
					<span className="font-mono text-sm">
						{fr.destinationAddress.slice(0, 8)}...
						{fr.destinationAddress.slice(-6)}
					</span>
				),
			},
			{
				id: "created",
				header: "Created",
				cell: (fr) => <TimeLabel time={fr.createdAt} />,
			},
			{
				id: "actions",
				header: "",
				className: "w-[100px]",
				cell: (fr) => (
					<div onClick={(e) => e.stopPropagation()}>
						{fr.status === "pending" ? (
							<Button size="sm" onClick={() => handleOpenBroadcastDialog(fr)}>
								<Radio className="mr-1 h-3 w-3" />
								Broadcast
							</Button>
						) : fr.transactionHash ? (
							<Button size="sm" variant="ghost" asChild>
								<a
									href={`https://blockscan.com/tx/${fr.transactionHash}`}
									target="_blank"
									rel="noopener noreferrer"
								>
									<ExternalLink className="mr-1 h-3 w-3" />
									View
								</a>
							</Button>
						) : null}
					</div>
				),
			},
		],
		[handleOpenBroadcastDialog],
	);

	return (
		<>
			<div className="p-4 sm:p-6">
				<h1 className="text-3xl font-bold mb-6">Fund Returns</h1>

				<div className="flex items-center border-b mb-6">
					<Link
						to={buildTabLink("wallets")}
						className={cn(
							"px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px",
							activeTab === "wallets"
								? "border-primary text-foreground"
								: "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground",
						)}
					>
						Blocked Wallets ({walletAssetRows.length})
					</Link>
					<Link
						to={buildTabLink("returns")}
						className={cn(
							"px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px",
							activeTab === "returns"
								? "border-primary text-foreground"
								: "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground",
						)}
					>
						Fund Returns ({totalReturns})
					</Link>
				</div>

				{activeTab === "wallets" ? (
					<DataTable
						columns={walletColumns}
						data={walletAssetRows}
						isLoading={isLoadingWallets}
						onRowClick={handleWalletRowClick}
						getRowId={(row) => row.rowKey}
						emptyMessage="No blocked wallets"
						emptyDescription="Blocked wallets with balances will appear here"
						emptyIcon={<Wallet className="h-8 w-8" />}
					/>
				) : (
					<div className="space-y-4">
						<div className="flex gap-2">
							<Select
								value={statusFilter}
								onValueChange={(v) =>
									setFilters({ status: v === "all" ? "" : v, page: 1 })
								}
							>
								<SelectTrigger className="w-[180px]">
									<SelectValue placeholder="Filter by status" />
								</SelectTrigger>
								<SelectContent>
									{STATUS_OPTIONS.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{statusFilter !== "all" && (
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setFilters({ status: "", page: 1 })}
								>
									Clear
								</Button>
							)}
						</div>

						<DataTable
							columns={returnColumns}
							data={fundReturns}
							isLoading={isLoadingReturns}
							onRowClick={handleReturnRowClick}
							getRowId={(row) => String(row.id)}
							emptyMessage="No fund returns"
							emptyDescription={
								statusFilter !== "all"
									? "No fund returns match the selected filter"
									: "Fund returns will appear here once created"
							}
							emptyIcon={<Wallet className="h-8 w-8" />}
							pagination={
								totalReturns > 0
									? {
											page: filters.page,
											pageSize: filters.limit,
											totalCount: totalReturns,
											onPageChange: (page: number) => setFilters({ page }),
											onPageSizeChange: (limit: number) =>
												setFilters({ limit, page: 1 }),
										}
									: undefined
							}
						/>
					</div>
				)}

				<Dialog
					open={showBroadcastDialog}
					onOpenChange={(open) => !open && handleCloseBroadcastDialog()}
				>
					<DialogContent className="sm:max-w-[400px]">
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<Radio className="h-5 w-5" />
								Broadcast Transaction
							</DialogTitle>
							<DialogDescription>
								This will broadcast the transaction to the blockchain. This
								action is irreversible.
							</DialogDescription>
						</DialogHeader>

						{selectedReturn && (
							<div className="space-y-4 py-4">
								<div className="rounded-lg border p-3 bg-muted/50 space-y-2 text-sm">
									<div className="flex justify-between">
										<span className="text-muted-foreground">Amount</span>
										<span className="font-medium">
											{selectedReturn.amount} {selectedReturn.currency}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">To</span>
										<span className="font-mono text-xs">
											{selectedReturn.destinationAddress.slice(0, 12)}...
										</span>
									</div>
								</div>

								<div className="space-y-2">
									<Label htmlFor="broadcast-totp">2FA Code</Label>
									<Input
										id="broadcast-totp"
										placeholder="6-digit code"
										value={totpCode}
										onChange={(e) =>
											setTotpCode(e.target.value.replace(/\D/g, ""))
										}
										disabled={broadcastMutation.isPending}
										maxLength={6}
										inputMode="numeric"
									/>
								</div>

								<div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
									<p className="text-sm text-red-800 dark:text-red-200">
										This action cannot be undone.
									</p>
								</div>
							</div>
						)}

						<DialogFooter>
							<Button
								variant="outline"
								onClick={handleCloseBroadcastDialog}
								disabled={broadcastMutation.isPending}
							>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={handleBroadcast}
								disabled={broadcastMutation.isPending || totpCode.length !== 6}
							>
								{broadcastMutation.isPending ? "Broadcasting..." : "Broadcast"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			<Outlet
				context={{
					fundReturns,
					isLoading: returnsQuery.isLoading,
					blockedWallets,
					isLoadingWallets,
				}}
			/>
		</>
	);
};

export type FundReturnsOutletContext = {
	fundReturns: FundReturn[];
	isLoading: boolean;
	blockedWallets: BlockedWallet[];
	isLoadingWallets: boolean;
};
